import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BlogPost, useBlogPosts } from "@/data/blogPosts";
import { Clock, Calendar, Tag } from "lucide-react";

const BlogIndex = () => {
  const categories = ["Budget Optimization", "Google Ads Tips", "Agency Tips", "AI & MCP"];
  const { posts: blogPosts, loading } = useBlogPosts();

  const renderPostCard = (post: BlogPost) => (
    <Link key={post.slug} to={`/blog/${post.slug}`}>
      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
        <div className="h-48 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-3 h-3 text-blue-600" />
            <span className="text-xs font-semibold text-blue-600 uppercase">{post.category}</span>
          </div>
          <CardTitle className="text-xl line-clamp-2">{post.title}</CardTitle>
          <CardDescription className="line-clamp-2">{post.excerpt}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.readTime} min read
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Blog | Google Ads Tips, Budget Optimization & Agency Strategies | Upfixe</title>
        <meta name="description" content="Read our latest articles on Google Ads optimization, budget management, CPA reduction, and agency scaling strategies." />
        <meta name="keywords" content="Google Ads blog, budget optimization, agency tips, CPA reduction, keyword filtering" />
        <meta property="og:title" content="Blog | Google Ads Tips & Optimization | Upfixe" />
        <meta property="og:description" content="Read our latest articles on Google Ads optimization, budget management, and agency scaling." />
        <link rel="canonical" href="https://upfixe.com/blog" />
      </Helmet>
      <Header />

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Google Ads Mastery</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Real strategies for reducing CPA, optimizing budgets, and scaling Google Ads at any business size.
            </p>
          </div>

          {/* Blog Posts Grid */}
          {loading && blogPosts.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-80 rounded-lg bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
              {blogPosts.map((post) => renderPostCard(post))}
            </div>
          )}

          {/* Category Breakdown */}
          <div className="bg-blue-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Explore by Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {categories.map((category) => {
                const count = blogPosts.filter((p) => p.category === category).length;
                return (
                  <div key={category} className="bg-white rounded-lg p-6">
                    <h3 className="font-bold text-lg mb-2">{category}</h3>
                    <p className="text-muted-foreground mb-4">{count} articles</p>
                    <Button variant="outline" asChild>
                      <Link to={`/blog?category=${category}`}>View All</Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogIndex;
